import React, { useState, useCallback } from 'react';
import ShipDateDropdown from '../../sharedComponents/ShipDateDropdown';
import WeeklyShipService from './WeeklyShipService';
import NotArrivedItemsModal from './NotArrivedItemsModal';
import DealerUnscannedModal from './DealerUnscannedModal';
import HeldDealerItemsModal from './HeldDealerItemsModal';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import ImageIcon from '@mui/icons-material/Image';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

const WeeklyShipAirManage = () => {
  const [shipDateId, setShipDateId] = useState('');
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, box: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, box: null });
  const [notArrivedConfirm, setNotArrivedConfirm] = useState({ open: false, box: null });
  const [notArrivedListOpen, setNotArrivedListOpen] = useState(false);
  const [unscannedOpen, setUnscannedOpen] = useState(false);
  const [heldDealerOpen, setHeldDealerOpen] = useState(false);
  const [imageModal, setImageModal] = useState({ open: false, box: null });
  const [moveModal, setMoveModal] = useState({ open: false, box: null });
  const [moveTargetId, setMoveTargetId] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ boxNo: '', weight: '', trackingJp: '' });

  const loadBoxes = useCallback(async (id, { silent = false } = {}) => {
    if (!id || id === '' || id === 'all' || id === 'none') {
      setBoxes([]);
      return [];
    }
    if (!silent) setLoading(true);
    try {
      const res = await WeeklyShipService.getWeeklyShipAirDetailByShipDateId(parseInt(id, 10));
      const list = Array.isArray(res.data) ? res.data : [];
      const sorted = list.sort((a, b) => (a.boxNo != null && b.boxNo != null ? String(a.boxNo).localeCompare(String(b.boxNo), undefined, { numeric: true }) : 0));
      setBoxes(sorted);
      return sorted;
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ');
      setBoxes([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const handleShipDateChange = (e) => {
    const id = e.target.value;
    setShipDateId(id);
    loadBoxes(id);
  };

  const openEdit = (box) => {
    setEditModal({ open: true, box });
    setForm({
      boxNo: box.boxNo != null ? String(box.boxNo) : '',
      weight: box.weight != null ? String(box.weight) : '',
      trackingJp: box.trackingJp || '',
    });
  };

  const closeEdit = () => {
    setEditModal({ open: false, box: null });
    setForm({ boxNo: '', weight: '', trackingJp: '' });
  };

  const handleEditSave = async () => {
    const { box } = editModal;
    if (!box?.weeklyShipId) return;
    if (!form.weight.trim()) {
      toast.warning('กรุณาใส่น้ำหนัก');
      return;
    }
    setSaving(true);
    try {
      await WeeklyShipService.updateAirWeeklyShip({
        id: box.weeklyShipId,
        boxNo: form.boxNo.trim() ? form.boxNo.trim() : undefined,
        weight: form.weight.trim() || null,
        trackingJp: form.trackingJp.trim() || null,
      });
      toast.success('แก้ไขสำเร็จ');
      closeEdit();
      loadBoxes(shipDateId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'แก้ไขไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteConfirm = (box) => setDeleteConfirm({ open: true, box });
  const closeDeleteConfirm = () => setDeleteConfirm({ open: false, box: null });
  const openImageModal = (box) => setImageModal({ open: true, box });
  const closeImageModal = () => setImageModal({ open: false, box: null });
  const openMove = (box) => { setMoveTargetId(''); setMoveModal({ open: true, box }); };
  const closeMove = () => { setMoveModal({ open: false, box: null }); setMoveTargetId(''); };

  const handleMove = async () => {
    const { box } = moveModal;
    if (!box?.uuid) return;
    if (!moveTargetId || moveTargetId === 'all' || moveTargetId === 'none') {
      toast.warning('กรุณาเลือกรอบปลายทาง');
      return;
    }
    if (String(moveTargetId) === String(shipDateId)) {
      toast.warning('กล่องอยู่ในรอบนี้อยู่แล้ว');
      return;
    }
    setSaving(true);
    try {
      await WeeklyShipService.moveAirWeeklyShip({ weeklyShipUuid: box.uuid, shipDateId: moveTargetId });
      toast.success('ย้ายรอบสำเร็จ');
      closeMove();
      loadBoxes(shipDateId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ย้ายรอบไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const { box } = deleteConfirm;
    if (!box?.uuid) return;
    setSaving(true);
    try {
      await WeeklyShipService.deleteAirWeeklyShip([box.uuid]);
      toast.success('ลบสำเร็จ');
      closeDeleteConfirm();
      loadBoxes(shipDateId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ลบไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const openNotArrivedConfirm = (box) => setNotArrivedConfirm({ open: true, box });
  const closeNotArrivedConfirm = () => setNotArrivedConfirm({ open: false, box: null });

  // Reverse a dealer box out of this round: the parcel didn't physically arrive. It parks
  // as a locked "not-arrived" box (remembers its order) and re-enters via the scan station
  // when it turns up in a later batch.
  const handleMarkNotArrived = async () => {
    const { box } = notArrivedConfirm;
    if (!box?.uuid) return;
    setSaving(true);
    try {
      await WeeklyShipService.markDealerBoxNotArrived(box.uuid);
      toast.success('ทำเครื่องหมาย "ยังไม่มาส่ง" แล้ว');
      closeNotArrivedConfirm();
      loadBoxes(shipDateId);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'ทำเครื่องหมายไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        แก้ไข/ลบ กล่องแอร์
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <ShipDateDropdown
          airOnly={true}
          manage={true}
          value={shipDateId}
          handleDataChange={handleShipDateChange}
          includeDefault={true}
        />
        <Button
          variant="outlined"
          color="warning"
          startIcon={<ReportProblemOutlinedIcon />}
          onClick={() => setNotArrivedListOpen(true)}
        >
          พัสดุยังไม่มาส่ง
        </Button>
        <Button
          variant="outlined"
          onClick={() => setHeldDealerOpen(true)}
        >
          พัสดุพัก (HOLD)
        </Button>
        <Button
          variant="outlined"
          startIcon={<FactCheckOutlinedIcon />}
          disabled={!shipDateId || shipDateId === 'all' || shipDateId === 'none'}
          onClick={() => setUnscannedOpen(true)}
        >
          ตรวจกล่องยังไม่สแกน
        </Button>
        {loading && <CircularProgress size={24} />}
      </Box>

      {!shipDateId || shipDateId === 'all' || shipDateId === 'none' ? (
        <Typography color="text.secondary">เลือกรอบเรือ (เครื่องบิน) เพื่อดูและจัดการกล่อง</Typography>
      ) : (
        <Box>
          {boxes.map((b) => (
            <Paper
              key={b.weeklyShipId}
              sx={{
                mb: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Typography fontWeight="bold">กล่องที่ {b.boxNo}</Typography>
                <Typography color="text.secondary">น้ำหนัก: {b.weight != null ? `${b.weight} kg` : '-'}</Typography>
                <Typography color="text.secondary">เลขพัสดุ JP: {b.trackingJp || '-'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton color="info" onClick={() => openImageModal(b)} aria-label="ดูรูป" size="small" title="ดูรูปกล่อง">
                  <ImageIcon />
                </IconButton>
                <IconButton color="primary" onClick={() => openEdit(b)} aria-label="แก้ไข" size="small">
                  <EditIcon />
                </IconButton>
                <span title={b.orders?.length > 0 ? 'จับคู่ออเดอร์แล้ว — ยกเลิกการจับคู่ก่อนย้าย' : 'ย้ายไปรอบอื่น'}>
                  <IconButton
                    color="secondary"
                    onClick={() => openMove(b)}
                    aria-label="ย้ายรอบ"
                    size="small"
                    disabled={b.orders?.length > 0}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </span>
                {b.dealerBoxId && (
                  <IconButton color="warning" onClick={() => openNotArrivedConfirm(b)} aria-label="ยังไม่มาส่ง" size="small" title="พัสดุยังไม่มาส่ง (ตกรอบ)">
                    <ReportProblemOutlinedIcon />
                  </IconButton>
                )}
                <IconButton color="error" onClick={() => openDeleteConfirm(b)} aria-label="ลบ" size="small">
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
          {boxes.length === 0 && !loading && (
            <Typography color="text.secondary">ไม่มีกล่องแอร์สำหรับรอบเรือนี้</Typography>
          )}
        </Box>
      )}

      <Dialog open={editModal.open} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>แก้ไขกล่องแอร์</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="เลขกล่อง"
              value={form.boxNo}
              onChange={(e) => setForm((f) => ({ ...f, boxNo: e.target.value }))}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            <TextField
              label="น้ำหนัก (kg) *"
              value={form.weight}
              onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
              required
            />
            <TextField
              label="เลขพัสดุ JP"
              value={form.trackingJp}
              onChange={(e) => setForm((f) => ({ ...f, trackingJp: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saving}>
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveModal.open} onClose={closeMove} maxWidth="sm" fullWidth>
        <DialogTitle>ย้ายกล่องแอร์ไปรอบอื่น</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ mb: 2 }} color="text.secondary">
              ย้ายกล่องที่ {moveModal.box?.boxNo} (เลขพัสดุ JP: {moveModal.box?.trackingJp || '-'}) ไปยังรอบบิน:
            </Typography>
            <ShipDateDropdown
              airOnly={true}
              manage={true}
              value={moveTargetId}
              handleDataChange={(e) => setMoveTargetId(e.target.value)}
              includeDefault={true}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMove}>ยกเลิก</Button>
          <Button variant="contained" onClick={handleMove} disabled={saving}>
            {saving ? 'กำลังย้าย...' : 'ย้าย'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirm.open} onClose={closeDeleteConfirm}>
        <DialogTitle>ยืนยันการลบ</DialogTitle>
        <DialogContent>
          <Typography>
            ลบกล่องที่ {deleteConfirm.box?.boxNo} (น้ำหนัก {deleteConfirm.box?.weight} kg)? รูปจะถูกปล่อยกลับไปให้ใส่น้ำหนักใหม่ได้ หากกล่องนี้จับคู่กับออเดอร์แล้ว ระบบจะยกเลิกการจับคู่ให้อัตโนมัติ
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirm}>ยกเลิก</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? 'กำลังลบ...' : 'ลบ'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={notArrivedConfirm.open} onClose={closeNotArrivedConfirm}>
        <DialogTitle>ยืนยัน "ยังไม่มาส่ง"</DialogTitle>
        <DialogContent>
          <Typography>
            กล่องที่ {notArrivedConfirm.box?.boxNo} (Dealer #{notArrivedConfirm.box?.dealerBoxNo}) ยังไม่มาส่งในรอบนี้?
            ออเดอร์จะถูกนำออกจากรอบ และกล่องจะถูกพักไว้ (ล็อก) จนกว่าจะสแกนเข้ารอบที่มาถึงจริง
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNotArrivedConfirm}>ยกเลิก</Button>
          <Button variant="contained" color="warning" onClick={handleMarkNotArrived} disabled={saving}>
            {saving ? 'กำลังทำเครื่องหมาย...' : 'ยังไม่มาส่ง'}
          </Button>
        </DialogActions>
      </Dialog>

      {notArrivedListOpen && <NotArrivedItemsModal onClose={() => setNotArrivedListOpen(false)} />}

      {heldDealerOpen && <HeldDealerItemsModal onClose={() => setHeldDealerOpen(false)} />}

      {unscannedOpen && (
        <DealerUnscannedModal
          shipDateId={shipDateId}
          onClose={() => setUnscannedOpen(false)}
          onChanged={() => loadBoxes(shipDateId)}
        />
      )}

      <Dialog open={imageModal.open} onClose={closeImageModal} maxWidth="md" fullWidth>
        <DialogTitle>รูปกล่องที่ {imageModal.box?.boxNo}</DialogTitle>
        <DialogContent>
          {imageModal.box?.shipImageUrl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
              <img
                src={imageModal.box.shipImageUrl}
                alt={`กล่องที่ ${imageModal.box.boxNo}`}
                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            </Box>
          ) : (
            <Typography color="text.secondary">ไม่มีรูปสำหรับกล่องนี้</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImageModal}>ปิด</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WeeklyShipAirManage;
